import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  ExportOutlined,
  FileTextOutlined,
  SearchOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import { api } from '../../api/client';
import { useEvalRoleLabels } from '../../contexts/EvalRoleLabelsContext';
import { useTalentPoolLabels } from '../../contexts/TalentPoolLabelsContext';
import { useImChannel } from '../../hooks/useImChannel';
import { adminContentCardStyle, adminFilterCardStyle } from '../../styles/adminPageShell';
import {
  loadAcks,
  loadNotes,
  saveAcks,
  saveNotes,
} from './evalRelationsAnomalyStorage';
import { buildEvalRelationsCsv, taskSourceLabel } from './evalRelationsExport';
import {
  allocateRowSlots,
  buildPivotRows,
  isAnomaly,
  pivotRowCompletionPercent,
  rowHasAnomaly,
  rowHasMissing,
  rowKeyOf,
  type ApiGroup,
  type EvaluatorRef,
  type PivotRow,
  type RowAllocation,
} from './evalRelationsPivot';
import {
  buildPoolColDefs,
  getDefaultCreatePick,
  filterColsByDisabledRoles,
  POOL_COL_DEFS,
  poolDescOf,
  type ColDef,
} from './poolColDefs';
import type { CreateTarget, EditTarget } from './EvalRelationsEditModals';
import { EvalRelationsImportPanel } from './EvalRelationsImportPanel';
import { EvalRelationsOrgSyncModal } from './EvalRelationsOrgSyncModal';

interface Props {
  batches: Array<{ id: number; name: string; phase?: string }>;
  batchId: number | undefined;
  setBatchId: (batchId: number) => void;
  poolOrder: string[];
  onEdit: (target: EditTarget) => void;
  onCreate: (target: CreateTarget) => void;
}

const SEARCH_FIELDS = [
  { value: 'all', label: '全部字段' },
  { value: 'name', label: '被评价人' },
  { value: 'dept', label: '部门' },
  { value: 'evaluator', label: '评价人' },
];
const ROW_FILTERS = [
  { value: 'all', label: '全部' },
  { value: 'missing', label: '只看缺失' },
  { value: 'anomaly', label: '只看异常' },
];

/** 支持列头「小闪电」随机补派的角色 → 弹窗/提示文案（候选范围）。 */
const RANDOM_ASSIGN_ROLES = ['SAME_DEPT', 'CROSS_DEPT', 'PEER_LEADER', 'SUBORDINATE'] as const;
type RandomAssignRole = (typeof RANDOM_ASSIGN_ROLES)[number];
const RANDOM_ROLE_LABEL: Record<RandomAssignRole, string> = {
  SAME_DEPT: '本部门同事',
  CROSS_DEPT: '跨部门同事',
  PEER_LEADER: '同级同事',
  SUBORDINATE: '下属',
};
const RANDOM_ROLE_CANDIDATE_DESC: Record<RandomAssignRole, string> = {
  SAME_DEPT: '同部门在职员工',
  CROSS_DEPT: '同门店内其他部门在职员工（不跨门店）',
  PEER_LEADER: '同门店 + 同职级（人才池字典配置的职级）在职员工',
  SUBORDINATE: '该员工的在职下属（花名册直属 + 同分组同门店职级推断）',
};

export function EvalRelationsTable({
  batches,
  batchId,
  setBatchId,
  poolOrder,
  onEdit,
  onCreate,
}: Props) {
  const { label: imLabel } = useImChannel();
  const { getEvalRoleLabel, roles: evalRoleRows } = useEvalRoleLabels();
  const { getTalentPoolLabel, getTalentPoolColor, getTalentPoolColorHex } = useTalentPoolLabels();
  const [activePool, setActivePool] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [rowFilter, setRowFilter] = useState('all');
  const [completionFilter, setCompletionFilter] = useState<
    'all' | 'notStarted' | 'inProgress' | 'done'
  >('all');
  const [acks, setAcks] = useState(() => loadAcks());
  const [notes, setNotes] = useState(() => loadNotes());
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [randomPeersRole, setRandomPeersRole] = useState<
    'SAME_DEPT' | 'CROSS_DEPT' | 'PEER_LEADER' | 'SUBORDINATE' | null
  >(null);
  const [randomPeersMax, setRandomPeersMax] = useState(1);
  const [randomPeersBusy, setRandomPeersBusy] = useState(false);
  const queryClient = useQueryClient();
  const batchPhase = batches.find((batch) => batch.id === batchId)?.phase;
  const isSubordinateResampleLocked = batchPhase !== undefined && batchPhase !== 'DRAFT';
  const [noteTarget, setNoteTarget] = useState<EditTarget | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [orgSyncOpen, setOrgSyncOpen] = useState(false);

  useEffect(() => {
    if (poolOrder.length && !poolOrder.includes(activePool)) setActivePool(poolOrder[0]);
  }, [poolOrder, activePool]);

  const { data: groups = [], isLoading } = useQuery<ApiGroup[]>({
    queryKey: ['eval-grouped-by-evaluator', batchId],
    queryFn: () => api.get('/admin/eval-tasks/grouped-by-evaluator', { params: { batchId } }),
    enabled: !!batchId,
  });
  const { data: qualityTemplates = [] } = useQuery<
    Array<{ pool: string; relationWeights: Record<string, number> }>
  >({
    queryKey: ['quality-templates', batchId],
    queryFn: () => api.get('/admin/quality-templates', { params: { batchId } }),
    enabled: !!batchId,
  });

  // 评价角色字典总开关：停用的角色整列隐藏
  const disabledRoles = useMemo(
    () => new Set(evalRoleRows.filter((row) => !row.enabled).map((row) => row.code)),
    [evalRoleRows],
  );
  const poolColDefsMap = useMemo(() => {
    const map: Record<string, ColDef[]> = {};
    qualityTemplates.forEach((template) => {
      map[template.pool] = filterColsByDisabledRoles(
        buildPoolColDefs(template.pool, template.relationWeights, getEvalRoleLabel),
        disabledRoles,
      );
    });
    poolOrder.forEach((pool) => {
      map[pool] ||= filterColsByDisabledRoles(
        buildPoolColDefs(pool, undefined, getEvalRoleLabel),
        disabledRoles,
      );
    });
    return map;
  }, [qualityTemplates, poolOrder, getEvalRoleLabel, disabledRoles]);
  const getColDefs = useCallback(
    (pool: string) =>
      poolColDefsMap[pool] ||
      filterColsByDisabledRoles(POOL_COL_DEFS[pool] || POOL_COL_DEFS.BACKEND, disabledRoles),
    [poolColDefsMap, disabledRoles],
  );

  const allRows = useMemo(() => buildPivotRows(groups), [groups]);
  const rowAssignments = useMemo(() => {
    const map = new Map<string, RowAllocation>();
    allRows.forEach((row) => map.set(rowKeyOf(row), allocateRowSlots(row, getColDefs(row.pool))));
    return map;
  }, [allRows, getColDefs]);
  const poolCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allRows.forEach((row) => { counts[row.pool] = (counts[row.pool] || 0) + 1; });
    return counts;
  }, [allRows]);

  const filteredRows = useMemo(
    () =>
      allRows.filter((row) => {
        if (row.pool !== activePool) return false;
        const allocation = rowAssignments.get(rowKeyOf(row));
        if (rowFilter === 'missing' && !rowHasMissing(allocation)) return false;
        if (
          rowFilter === 'anomaly' &&
          !rowHasAnomaly(row, getColDefs(row.pool), allocation, acks)
        ) return false;
        const completion = pivotRowCompletionPercent(row);
        if (completionFilter === 'done' && completion !== 100) return false;
        if (completionFilter === 'inProgress' && (completion === 0 || completion === 100)) return false;
        if (completionFilter === 'notStarted' && completion !== 0) return false;
        if (!searchText) return true;
        if (searchField === 'name') return row.evaluatee.name.includes(searchText);
        if (searchField === 'dept') return (row.evaluatee.deptPath || '').includes(searchText);
        const evaluatorMatches = Object.values(row.byRole).some((items) =>
          items.some((item) => item.evaluatorName.includes(searchText)),
        );
        if (searchField === 'evaluator') return evaluatorMatches;
        return (
          row.evaluatee.name.includes(searchText) ||
          (row.evaluatee.deptPath || '').includes(searchText) ||
          (row.evaluatee.position || '').includes(searchText) ||
          evaluatorMatches
        );
      }),
    [
      allRows,
      activePool,
      rowAssignments,
      rowFilter,
      completionFilter,
      searchText,
      searchField,
      getColDefs,
      acks,
    ],
  );

  const anomalyCount = useMemo(
    () =>
      filteredRows.reduce((count, row) => {
        const allocation = rowAssignments.get(rowKeyOf(row));
        return (
          count +
          getColDefs(row.pool).reduce((colCount, col, index) => {
            const evaluators = allocation?.perCol[index] ?? [];
            return (
              colCount +
              evaluators.filter((evaluator) => isAnomaly(row, col, evaluator, acks)).length
            );
          }, 0)
        );
      }, 0),
    [filteredRows, rowAssignments, getColDefs, acks],
  );
  const missingStats = useMemo(() => {
    let slots = 0;
    let people = 0;
    let unassignedSlots = 0;
    let unassignedPeople = 0;
    const poolRows = allRows.filter((row) => row.pool === activePool);
    poolRows.forEach((row) => {
      const allocation = rowAssignments.get(rowKeyOf(row));
      if (!allocation) return;
      const missing = allocation.perCol.filter((evaluators) => !evaluators.length).length;
      if (missing) {
        slots += missing;
        people += 1;
      }
      if (allocation.unassigned.length) {
        unassignedSlots += allocation.unassigned.length;
        unassignedPeople += 1;
      }
    });
    return { slots, people, unassignedSlots, unassignedPeople, poolTotal: poolRows.length };
  }, [allRows, activePool, rowAssignments]);
  const stats = useMemo(
    () => ({
      totalPeople: allRows.length,
      totalLinks: allRows.reduce((sum, row) => sum + row.allTasks.length, 0),
    }),
    [allRows],
  );

  const handleExport = () => {
    const csv = buildEvalRelationsCsv({
      poolOrder,
      allRows,
      getColDefs,
      getTalentPoolLabel,
      acks,
      notes,
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    anchor.download = `eval-relations-${timestamp}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    message.success(
      allRows.length
        ? `已导出 ${poolOrder.filter((pool) => poolCounts[pool]).length} 个池的评价关系`
        : `已导出 ${poolOrder.length} 个池的 CSV 表头（当前无评价关系数据）`,
    );
  };

  /** 「本部门/跨部门同事」列头按钮：按上限随机补派评价人（已有的保留）。 */
  const runRandomPeers = async () => {
    if (!batchId || !randomPeersRole) return;
    setRandomPeersBusy(true);
    try {
      const result = (await api.post('/admin/eval-tasks/random-peers', {
        batchId,
        role: randomPeersRole,
        maxPerEvaluatee: randomPeersMax,
      })) as unknown as {
        mode?: 'replace';
        created: number;
        restored?: number;
        replaced?: number;
        evaluatees: number;
        skipped: Array<{ evaluateeName: string; reason: string }>;
      };
      if (randomPeersRole === 'SUBORDINATE' && result.mode === 'replace') {
        const parts = [
          result.created ? `新建 ${result.created}` : '',
          result.restored ? `恢复 ${result.restored}` : '',
          result.replaced ? `替换 ${result.replaced}` : '',
        ].filter(Boolean);
        message.success(
          `已重新抽选下属（覆盖 ${result.evaluatees} 人${parts.length ? `：${parts.join(' / ')}` : ''}）` +
            (result.skipped.length ? `，${result.skipped.length} 人跳过` : ''),
        );
      } else {
        message.success(
          `已随机补派 ${result.created} 条（覆盖 ${result.evaluatees} 人）` +
            (result.skipped.length ? `，${result.skipped.length} 人无法补派` : ''),
        );
      }
      if (result.skipped.length) {
        Modal.info({
          title:
            randomPeersRole === 'SUBORDINATE'
              ? '以下被评价人未能重新抽选下属'
              : '以下被评价人未能随机补派',
          content: (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 240, overflow: 'auto' }}>
              {result.skipped.map((item, index) => (
                <li key={index}>{item.evaluateeName}：{item.reason}</li>
              ))}
            </ul>
          ),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tasks-admin'] });
      setRandomPeersRole(null);
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? e?.message ?? '随机补派失败');
    } finally {
      setRandomPeersBusy(false);
    }
  };

  const renderSingleEvaluator = (
    row: PivotRow,
    col: ColDef,
    evaluator: EvaluatorRef,
    isEditing: boolean,
  ) => {
    const self = evaluator.evaluatorId === row.evaluatee.id;
    const anomaly = isAnomaly(row, col, evaluator, acks);
    const note = notes[evaluator.taskId];
    const sourceText = taskSourceLabel(evaluator.source);
    const name = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          color: anomaly ? '#cf1322' : self ? '#999' : '#262626',
          fontWeight: anomaly ? 600 : 400,
        }}
      >
        {anomaly && <WarningFilled style={{ color: '#ff4d4f', fontSize: 12 }} />}
        {evaluator.evaluatorName}
        {self && <span style={{ color: '#bfbfbf', fontSize: 12 }}>（本人）</span>}
        {evaluator.source && evaluator.source !== 'MANUAL' && (
          <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>{sourceText}</Tag>
        )}
      </span>
    );
    return (
      <Space key={evaluator.taskId} size={2} wrap>
        <Tooltip
          title={
            isEditing
              ? '点击改派评价人 / 评价关系'
              : `${evaluator.evaluatorName}｜${evaluator.deptPath || '—'}｜${evaluator.position || '—'}｜状态:${evaluator.status}｜来源:${sourceText}${evaluator.overrideReason ? `｜覆盖原因:${evaluator.overrideReason}` : ''}`
          }
        >
          <span
            onClick={isEditing ? () => onEdit({ row, col, current: evaluator }) : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: isEditing ? '2px 6px' : 0,
              border: isEditing ? '1px dashed #1677ff' : '1px solid transparent',
              borderRadius: 4,
              cursor: isEditing ? 'pointer' : 'default',
              background: isEditing ? '#e6f4ff' : 'transparent',
              transition: 'all .15s',
            }}
          >
            {name}
            {isEditing && <EditOutlined style={{ color: '#1677ff', fontSize: 12 }} />}
          </span>
        </Tooltip>
        {anomaly && (
          <Tooltip title={note ? `已填备注：${note}` : '填写异常备注'}>
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined style={{ color: note ? '#fa8c16' : '#999' }} />}
              onClick={() => {
                setNoteTarget({ row, col, current: evaluator });
                setNoteDraft(note || '');
              }}
            />
          </Tooltip>
        )}
        {anomaly && (
          <Tooltip title="确认（不再标记为异常）">
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              onClick={() => {
                const next = new Set(acks);
                next.add(evaluator.taskId);
                saveAcks(next);
                setAcks(next);
                message.success('已确认，本条评价关系不再标记为异常');
              }}
            />
          </Tooltip>
        )}
      </Space>
    );
  };

  const renderEvaluatorCell = (
    row: PivotRow,
    col: ColDef,
    evaluators: RowAllocation['perCol'][number] | undefined,
    isEditing: boolean,
  ) => {
    const list = evaluators ?? [];
    if (!list.length) {
      const defaults = getDefaultCreatePick(row.evaluatee.id, col);
      return (
        <Tooltip title={`「${col.label}」未派人，点击补派`}>
          <Button
            size="small"
            type="primary"
            danger
            ghost
            icon={<WarningFilled />}
            style={{ fontSize: 12, fontWeight: 600 }}
            onClick={() =>
              onCreate({
                row,
                col,
                defaultEvaluatorId: defaults.evaluatorId,
                defaultRole: defaults.role,
              })
            }
          >
            缺失·补派
          </Button>
        </Tooltip>
      );
    }
    return (
      <div style={{ display: 'grid', gap: 6 }}>
        {list.map((evaluator) => renderSingleEvaluator(row, col, evaluator, isEditing))}
        {isEditing && (
          <Button
            size="small"
            type="dashed"
            onClick={() => {
              const defaults = getDefaultCreatePick(row.evaluatee.id, col);
              onCreate({
                row,
                col,
                defaultEvaluatorId: defaults.evaluatorId,
                defaultRole: defaults.role,
              });
            }}
          >
            再补一人
          </Button>
        )}
      </div>
    );
  };

  const buildColumns = (pool: string) => {
    const cols = getColDefs(pool);
    return [
      {
        title: '#',
        key: 'idx',
        width: 50,
        render: (_value: unknown, _row: PivotRow, index: number) => (
          <span style={{ color: '#bfbfbf', fontSize: 12 }}>{index + 1}</span>
        ),
      },
      {
        title: '被评价人',
        key: 'name',
        width: 140,
        render: (_value: unknown, row: PivotRow) => (
          <div>
            <div style={{ fontWeight: 600 }}>{row.evaluatee.name}</div>
            <div style={{ color: '#888', fontSize: 12 }}>{row.evaluatee.position || '—'}</div>
          </div>
        ),
      },
      {
        title: '部门',
        key: 'dept',
        width: 160,
        render: (_value: unknown, row: PivotRow) => (
          <span style={{ color: '#666', fontSize: 12 }}>{row.evaluatee.deptPath || '—'}</span>
        ),
      },
      ...cols.map((col, index) => {
        const randomRole = RANDOM_ASSIGN_ROLES.find((role) => col.roles.includes(role));
        return {
          title: (
            <Space size={4}>
              <Tag color={getTalentPoolColor(pool)} style={{ margin: 0 }}>
                {col.label}
              </Tag>
              {randomRole && (
                <Tooltip
                  title={
                    randomRole === 'SUBORDINATE' && isSubordinateResampleLocked
                      ? '仅批次未开启时可重新抽选'
                      : randomRole === 'SUBORDINATE'
                        ? `重新抽选${RANDOM_ROLE_LABEL[randomRole]}评价人（候选：${RANDOM_ROLE_CANDIDATE_DESC[randomRole]}）`
                        : `随机补派${RANDOM_ROLE_LABEL[randomRole]}评价人（候选：${RANDOM_ROLE_CANDIDATE_DESC[randomRole]}）`
                  }
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<ThunderboltOutlined />}
                    disabled={
                      !batchId ||
                      (randomRole === 'SUBORDINATE' && isSubordinateResampleLocked)
                    }
                    onClick={() => setRandomPeersRole(randomRole)}
                  />
                </Tooltip>
              )}
            </Space>
          ),
          key: `col-${index}-${col.label}-${col.keySuffix || ''}`,
          render: (_value: unknown, row: PivotRow) =>
            renderEvaluatorCell(
              row,
              col,
              rowAssignments.get(rowKeyOf(row))?.perCol[index],
              editingRowKey === rowKeyOf(row),
            ),
        };
      }),
      {
        title: '操作',
        key: 'op',
        width: 110,
        fixed: 'right' as const,
        render: (_value: unknown, row: PivotRow) => {
          const key = rowKeyOf(row);
          return editingRowKey === key ? (
            <Button size="small" type="primary" ghost onClick={() => setEditingRowKey(null)}>
              完成
            </Button>
          ) : (
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => setEditingRowKey(key)}
            >
              编辑
            </Button>
          );
        },
      },
    ];
  };

  const tabItems = poolOrder.map((pool) => ({
    key: pool,
    label: <Space size={6}><Tag color={getTalentPoolColor(pool)} style={{ margin: 0 }}>{getTalentPoolLabel(pool)}</Tag><span style={{ color: '#999', fontSize: 12 }}>{poolCounts[pool] || 0}</span></Space>,
    children: null,
  }));
  const clearSearch = () => { setSearchInput(''); setSearchText(''); };

  return (
    <>
      <Card style={adminFilterCardStyle} styles={{ body: { paddingBottom: 12 } }}>
        <Space size={16} wrap style={{ width: '100%' }}>
          <span style={{ color: '#595959' }}>批次：</span>
          <Select placeholder="选择批次" style={{ width: 220 }} options={batches.map((batch) => ({ label: batch.name, value: batch.id }))} value={batchId} onChange={setBatchId} />
          <span style={{ marginLeft: 8, color: '#595959' }}>完成情况：</span>
          <Select value={completionFilter} onChange={setCompletionFilter} style={{ width: 130 }} options={[{ value: 'all', label: '全部' }, { value: 'notStarted', label: '未开始' }, { value: 'inProgress', label: '进行中' }, { value: 'done', label: '已完成' }]} />
          <Space.Compact>
            <Select value={searchField} onChange={setSearchField} options={SEARCH_FIELDS} style={{ width: 130 }} />
            <Input placeholder="输入关键字…" style={{ width: 220 }} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} onPressEnter={() => setSearchText(searchInput.trim())} allowClear onClear={clearSearch} />
            <Button icon={<SearchOutlined />} type="primary" onClick={() => setSearchText(searchInput.trim())}>搜索</Button>
            {searchText && <Button icon={<CloseCircleOutlined />} onClick={clearSearch}>清空</Button>}
          </Space.Compact>
          <span style={{ marginLeft: 8, color: '#595959' }}>行筛选：</span>
          <Select value={rowFilter} onChange={setRowFilter} options={ROW_FILTERS} style={{ width: 140 }} />
          <Button icon={<ExportOutlined />} onClick={handleExport}>导出 CSV</Button>
          <Button icon={<TeamOutlined />} type="primary" ghost disabled={!batchId} onClick={() => setOrgSyncOpen(true)}>
            按组织关系生成 / 同步
          </Button>
          <EvalRelationsImportPanel batchId={batchId} batchName={batches.find((batch) => batch.id === batchId)?.name} />
        </Space>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={4}><Statistic title="被评价人总数（全池）" value={stats.totalPeople} /></Col>
          <Col span={4}><Statistic title="评价关系总条数（全池）" value={stats.totalLinks} /></Col>
          <Col span={4}><Statistic title="当前池" value={getTalentPoolLabel(activePool)} /></Col>
          <Col span={4}><Statistic title="当前池：缺失槽位 / 缺失人数" value={`${missingStats.slots} / ${missingStats.people}`} valueStyle={{ color: missingStats.slots > 0 ? '#fa541c' : undefined }} suffix={<span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>/ {missingStats.poolTotal} 人</span>} /></Col>
          <Col span={4}><Statistic title="当前池：异常 / 显示条数" value={`${anomalyCount} / ${filteredRows.length}`} valueStyle={{ color: anomalyCount > 0 ? '#cf1322' : undefined }} /></Col>
        </Row>
      </Card>

      <Card style={adminContentCardStyle} styles={{ body: { paddingTop: 16 } }}>
        <Tabs activeKey={activePool} onChange={setActivePool} items={tabItems} />
        <div style={{ margin: '0 0 16px', padding: '12px 16px', background: 'linear-gradient(90deg, rgba(114,46,209,0.06) 0%, rgba(22,119,255,0.05) 100%)', borderRadius: 10, fontSize: 13, color: '#475569', border: '1px solid rgba(114, 46, 209, 0.12)', borderLeft: `4px solid ${getTalentPoolColorHex(activePool)}` }}>
          {poolDescOf(activePool, getColDefs(activePool))}
        </div>
        {isLoading ? <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
          : filteredRows.length === 0 ? <Empty description={searchText ? `在「${getTalentPoolLabel(activePool)}」中找不到匹配「${searchText}」的记录` : rowFilter === 'missing' ? `「${getTalentPoolLabel(activePool)}」当前没有缺失评价人的被评价人` : rowFilter === 'anomaly' ? `「${getTalentPoolLabel(activePool)}」当前没有直属领导异常的被评价人` : `「${getTalentPoolLabel(activePool)}」暂无数据`} style={{ padding: 60 }} />
            : <Table<PivotRow> rowKey={rowKeyOf} dataSource={filteredRows} columns={buildColumns(activePool) as any} pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }} size="middle" bordered scroll={{ x: 'max-content' }} />}
      </Card>

      <Modal title="异常备注" open={noteTarget !== null} onCancel={() => setNoteTarget(null)} onOk={() => {
        if (!noteTarget) return;
        const next = { ...notes };
        if (noteDraft.trim()) next[noteTarget.current.taskId] = noteDraft.trim();
        else delete next[noteTarget.current.taskId];
        saveNotes(next);
        setNotes(next);
        setNoteTarget(null);
        message.success('备注已保存');
      }} okText="保存" cancelText="取消" destroyOnClose>
        {noteTarget && <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ color: '#666', fontSize: 13 }}>被评价人：<strong>{noteTarget.row.evaluatee.name}</strong>{' · '}列：<Tag color={getTalentPoolColor(noteTarget.row.pool)}>{noteTarget.col.label}</Tag>{' · '}评价人：<Tag>{noteTarget.current.evaluatorName}</Tag></div>
          <Input.TextArea rows={4} placeholder={`例如：该员工已转岗，新直属领导待 HR 同步${imLabel}后再修正`} value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} maxLength={300} showCount />
        </div>}
      </Modal>

      <EvalRelationsOrgSyncModal
        open={orgSyncOpen}
        batchId={batchId}
        onClose={() => setOrgSyncOpen(false)}
      />

      <Modal
        title={
          randomPeersRole === 'SUBORDINATE'
            ? '重新抽选下属'
            : `随机补派${randomPeersRole ? RANDOM_ROLE_LABEL[randomPeersRole] : ''}`
        }
        open={randomPeersRole !== null}
        onCancel={() => setRandomPeersRole(null)}
        onOk={runRandomPeers}
        confirmLoading={randomPeersBusy}
        okText={randomPeersRole === 'SUBORDINATE' ? '重新抽选' : '随机补派'}
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            每人最多评价人数：
            <InputNumber
              min={1}
              max={10}
              value={randomPeersMax}
              onChange={(value) => setRandomPeersMax(value ?? 1)}
              style={{ marginLeft: 8, width: 90 }}
            />
          </div>
          <div style={{ color: '#888', fontSize: 12, lineHeight: 1.8 }}>
            {randomPeersRole === 'SUBORDINATE' ? (
              <>
                对全部池生效：覆盖当前未开始的下属评价任务并在本批次保持选择（每次重新洗牌，不是补足）。
                候选为{RANDOM_ROLE_CANDIDATE_DESC.SUBORDINATE}；无在职下属的员工会跳过并列出原因。
                仅批次未开启（DRAFT）时可操作。
              </>
            ) : (
              <>
                对全部池生效：给每个被评价人补足到上限（已有的保留）。候选为
                {randomPeersRole ? RANDOM_ROLE_CANDIDATE_DESC[randomPeersRole] : ''}，
                排除本人、其直属领导和已评价过该员工的人；被评价人是领导时优先不选其下属，无人可选时才从下属里挑；候选人数不够上限就有多少派多少。
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
